import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { api } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { touristAttraction, breadcrumbList, faqPage, videoObject } from "../lib/schema";
import { useUser } from "../lib/userAuth";
import SafetyAccordion from "../components/SafetyAccordion";
import WaterfallCard from "../components/WaterfallCard";
import ReviewsSection from "../components/ReviewsSection";
import QuestionsSection from "../components/QuestionsSection";
import VisitInfo from "../components/VisitInfo";
import { Star, Heart, Share2, Bookmark, Image as ImageIcon, ChevronRight, MapPin, ArrowLeft, Navigation, Check, Footprints, Link2, Twitter, Facebook } from "lucide-react";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

function youtubeEmbedUrl(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}` : null;
}

export default function WaterfallDetail() {
  const { slug } = useParams();
  const { user, signIn } = useUser();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [visited, setVisited] = useState(false);
  const [visitCount, setVisitCount] = useState(0);
  const [visitBusy, setVisitBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [savedBusy, setSavedBusy] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareNote, setShareNote] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/waterfalls/${slug}`);
        setData(r.data);
      } catch {
        setData({ error: true });
      }
    })();
  }, [slug]);

  // Public visit count
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/waterfalls/${slug}/visit-count`);
        setVisitCount(r.data.count || 0);
      } catch { /* ignore */ }
    })();
  }, [slug]);

  // User's visited status
  useEffect(() => {
    if (!user) { setVisited(false); setSaved(false); return; }
    (async () => {
      try {
        const r = await api.get("/me/visits/check", { params: { slugs: slug } });
        setVisited(!!r.data[slug]);
      } catch { /* ignore */ }
      try {
        const b = await api.get("/me/bookmarks/check", { params: { slugs: slug } });
        setSaved(!!b.data[slug]);
      } catch { /* ignore */ }
    })();
  }, [user, slug]);

  const toggleSave = async () => {
    if (!user) {
      signIn(null, `Sign in to save ${data?.waterfall?.name || "this waterfall"} to your bookmarks and build your bucket list.`);
      return;
    }
    setSavedBusy(true);
    try {
      const r = await api.post(`/me/bookmarks/${slug}`);
      setSaved(r.data.bookmarked);
    } finally { setSavedBusy(false); }
  };

  const handleShare = async () => {
    const url = window.location.href;
    const title = data?.waterfall?.name || "Waterfall";
    // Try Web Share API on mobile
    if (navigator.share) {
      try {
        await navigator.share({ title, url, text: `Check out ${title} on WaterfallFinder` });
        return;
      } catch { /* fall through to menu */ }
    }
    setShareOpen((v) => !v);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setShareNote("Link copied!");
      setTimeout(() => setShareNote(""), 1800);
    } catch {
      setShareNote("Couldn't copy — long-press the URL bar to copy.");
      setTimeout(() => setShareNote(""), 2400);
    }
  };

  const toggleVisit = async () => {
    if (!user) {
      signIn(null, `Sign in to mark ${data?.waterfall?.name || "this waterfall"} as visited and keep a personal log of every fall you've seen.`);
      return;
    }
    setVisitBusy(true);
    try {
      const r = await api.post(`/me/visits/${slug}`);
      setVisited(r.data.visited);
      setVisitCount((c) => c + (r.data.visited ? 1 : -1));
    } finally { setVisitBusy(false); }
  };

  useSEO(
    data?.waterfall
      ? (() => {
          const wf = data.waterfall;
          const crumbs = [
            { name: "Home", path: "/" },
            { name: "Waterfalls", path: "/waterfalls" },
            { name: wf.country, path: `/best-waterfalls-in/country/${wf.country_slug}` },
            ...(wf.state ? [{ name: wf.state, path: `/best-waterfalls-in/state/${wf.state_slug}` }] : []),
            { name: wf.name, path: `/waterfalls/${wf.slug}` },
          ];
          const faqs = [];
          if (wf.why_famous) faqs.push({ question: `Why is ${wf.name} famous?`, answer: wf.why_famous });
          if (wf.swimming_allowed) faqs.push({ question: `Can you swim at ${wf.name}?`, answer: wf.swimming_allowed });
          if (wf.dog_rules) faqs.push({ question: `Are dogs allowed at ${wf.name}?`, answer: wf.dog_rules });
          if (wf.entry_fee) faqs.push({ question: `Is ${wf.name} free? Do you need a permit?`, answer: wf.entry_fee });
          if (wf.parking_info) faqs.push({ question: `Is there parking at ${wf.name}?`, answer: wf.parking_info });
          if (wf.hike_length) faqs.push({ question: `How long is the hike to ${wf.name}?`, answer: wf.hike_length });
          if (wf.hike_difficulty) faqs.push({ question: `${wf.name} hike difficulty`, answer: wf.hike_difficulty });
          if (wf.best_time_to_visit) faqs.push({ question: `When is the best time to visit ${wf.name}?`, answer: wf.best_time_to_visit });
          const blocks = [
            touristAttraction(wf),
            breadcrumbList(crumbs),
            faqPage(faqs),
            videoObject(wf),
          ].filter(Boolean);
          return {
            title: wf.meta_title || `${wf.name} | WaterfallFinder`,
            description: wf.meta_description || `Visit guide for ${wf.name}.`,
            image: wf.image_urls?.[0],
            jsonLd: blocks,
          };
        })()
      : { title: "Waterfall — WaterfallFinder" }
  );

  if (!data) return <main className="pt-16 max-w-7xl mx-auto p-8">Loading…</main>;
  if (data.error) return <main className="pt-16 max-w-7xl mx-auto p-8">Waterfall not found.</main>;

  const wf = data.waterfall;
  const embed = youtubeEmbedUrl(wf.youtube_url);
  const images = (wf.image_urls || []).filter(Boolean);
  const mainImg = images[0] || "https://images.unsplash.com/photo-1469125155630-7ed37e065743?w=1600";
  const hasSide = images.length > 1;
  const extraImages = images.slice(2);

  return (
    <main className="pt-16 pb-12" data-testid="waterfall-detail">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 pt-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-stone-600 mb-5 flex-wrap" data-testid="breadcrumbs">
          <Link to="/waterfalls" className="hover:underline font-medium text-stone-900 flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" /> Back to Explore
          </Link>
          <ChevronRight className="w-4 h-4 text-stone-400" />
          <Link to={`/best-waterfalls-in/country/${wf.country_slug}`} className="hover:underline">{wf.country}</Link>
          {wf.state && (
            <>
              <ChevronRight className="w-4 h-4 text-stone-400" />
              <Link to={`/best-waterfalls-in/state/${wf.state_slug}`} className="hover:underline">{wf.state}</Link>
            </>
          )}
          {wf.city && (
            <>
              <ChevronRight className="w-4 h-4 text-stone-400" />
              <Link to={`/best-waterfalls-in/city/${wf.city_slug}`} className="hover:underline">{wf.city}</Link>
            </>
          )}
          <ChevronRight className="w-4 h-4 text-stone-400" />
          <span className="text-stone-900 font-medium">{wf.name}</span>
        </nav>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-stone-900">{wf.name}</h1>
        <div className="mt-3 flex items-center gap-3 text-sm text-stone-700 flex-wrap">
          {wf.review_count > 0 ? (
            <span className="flex items-center gap-1">
              <Star className="w-4 h-4 fill-stone-900 text-stone-900" />
              <strong>{wf.rating?.toFixed(1)}</strong>
              <span className="text-stone-500">({wf.review_count.toLocaleString()} reviews)</span>
            </span>
          ) : (
            <span className="text-stone-500">No reviews yet</span>
          )}
          {visitCount > 0 && (
            <>
              <span className="text-stone-300">·</span>
              <span className="flex items-center gap-1 text-stone-700" data-testid="visit-count">
                <Footprints className="w-4 h-4" />
                <strong>{visitCount.toLocaleString()}</strong>
                <span className="text-stone-500">been here</span>
              </span>
            </>
          )}
          {wf.city && (
            <>
              <span className="text-stone-300">·</span>
              <Link to={`/best-waterfalls-in/city/${wf.city_slug}`} className="text-stone-700 underline">{wf.city}</Link>
            </>
          )}
          {wf.country && (
            <>
              <span className="text-stone-300">·</span>
              <Link to={`/best-waterfalls-in/country/${wf.country_slug}`} className="text-stone-700 underline">{wf.country}</Link>
            </>
          )}
        </div>

        {/* "I've been here" toggle */}
        <button
          onClick={toggleVisit}
          disabled={visitBusy}
          data-testid="visit-toggle"
          className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full border-2 text-sm font-semibold transition-colors disabled:opacity-50 ${
            visited
              ? "border-emerald-600 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              : "border-stone-300 text-stone-900 hover:border-stone-900"
          }`}
        >
          {visited ? <Check className="w-4 h-4" /> : <Footprints className="w-4 h-4" />}
          {visited ? "You've been here" : "I've been here"}
        </button>

        {/* Tabs + actions row. The Hiking/Swim/Dogs "tabs" scroll to their
            H2 anchor inside the Plan-Your-Visit section rather than switching
            content — so users get quick jumps to what they actually care about. */}
        <div className="mt-6 border-b border-stone-200 flex items-end justify-between" data-testid="detail-tabs">
          <div className="flex gap-6 overflow-x-auto no-scrollbar">
            {[
              { id: "overview", label: "Overview", kind: "tab" },
              { id: "hike_length", label: "Hiking", kind: "jump" },
              { id: "swimming_allowed", label: "Swimming", kind: "jump" },
              { id: "dog_rules", label: "Dogs", kind: "jump" },
              { id: "best_time_to_visit", label: "Best time", kind: "jump" },
              { id: "reviews", label: "Reviews", kind: "tab" },
              { id: "nearby", label: "Nearby", kind: "tab" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => {
                  if (t.kind === "tab") {
                    setTab(t.id);
                  } else {
                    // Jump — ensure overview is active so the anchor exists in DOM
                    setTab("overview");
                    setTimeout(() => {
                      const el = document.getElementById(`visit-${t.id}`);
                      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                    }, 60);
                  }
                }}
                data-testid={`tab-${t.id}`}
                className={`tab-link ${(t.kind === "tab" && tab === t.id) ? "active" : ""} whitespace-nowrap`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="hidden md:flex items-center gap-2 pb-2 relative">
            <button
              onClick={toggleSave}
              disabled={savedBusy}
              aria-label={saved ? "Remove bookmark" : "Save"}
              data-testid="detail-save-btn"
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors disabled:opacity-60 ${saved ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-stone-100 hover:bg-stone-200 text-stone-900"}`}
              title={saved ? "Saved" : "Save"}
            >
              <Bookmark className={`w-4 h-4 ${saved ? "fill-emerald-700" : ""}`} />
            </button>
            <button
              onClick={handleShare}
              aria-label="Share"
              data-testid="detail-share-btn"
              className="w-10 h-10 rounded-full bg-stone-100 hover:bg-stone-200 text-stone-900 flex items-center justify-center"
              title="Share"
            >
              <Share2 className="w-4 h-4" />
            </button>
            {shareOpen && (
              <div className="absolute right-0 top-12 z-40 w-64 bg-white rounded-xl shadow-xl border border-stone-200 p-2" data-testid="share-menu">
                <button onClick={copyLink} data-testid="share-copy" className="w-full text-left flex items-center gap-2 px-3 py-2 rounded-md hover:bg-stone-50 text-sm">
                  <Link2 className="w-4 h-4" /> Copy link
                </button>
                <a
                  href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(`Check out ${wf?.name || "this waterfall"} on WaterfallFinder`)}&url=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "")}`}
                  target="_blank" rel="noreferrer"
                  data-testid="share-twitter"
                  className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-stone-50 text-sm"
                  onClick={() => setShareOpen(false)}
                >
                  <Twitter className="w-4 h-4" /> Share on X / Twitter
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(typeof window !== "undefined" ? window.location.href : "")}`}
                  target="_blank" rel="noreferrer"
                  data-testid="share-facebook"
                  className="flex items-center gap-2 px-3 py-2 rounded-md hover:bg-stone-50 text-sm"
                  onClick={() => setShareOpen(false)}
                >
                  <Facebook className="w-4 h-4" /> Share on Facebook
                </a>
                {shareNote && <p className="px-3 py-2 text-xs text-emerald-700" data-testid="share-note">{shareNote}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Image grid (AllTrails-style). Adapts to 1 / 2 / 3+ images. */}
        <section className={`mt-6 detail-grid ${!hasSide ? "detail-grid-single" : ""}`} data-testid="detail-image-grid">
          <div>
            <img src={mainImg} alt={wf.name} />
            {images.length > 0 && (
              <div className="absolute left-3 bottom-3 bg-white/95 rounded-full px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5">
                <ImageIcon className="w-3.5 h-3.5" /> {images.length} photo{images.length !== 1 ? "s" : ""}
              </div>
            )}
          </div>
          {hasSide && (
            <div>
              <img src={images[1]} alt={wf.name} />
            </div>
          )}
          <div className="relative">
            {wf.lat && wf.lng ? (
              <MapContainer center={[wf.lat, wf.lng]} zoom={11} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
                <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                <Marker position={[wf.lat, wf.lng]}>
                  <Popup>{wf.name}</Popup>
                </Marker>
              </MapContainer>
            ) : (
              <img src={mainImg} alt="" />
            )}
          </div>
        </section>

        {/* Extra images row when there are more than 2 */}
        {extraImages.length > 0 && (
          <section className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="detail-image-extra">
            {extraImages.map((u, i) => (
              <div key={u + i} className="aspect-[4/3] rounded-xl overflow-hidden bg-stone-100">
                <img src={u} alt={`${wf.name} ${i + 3}`} className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </section>
        )}

        {/* Stat: only show height when admin has set it */}
        {wf.height_m > 0 && (
          <section className="mt-8 flex flex-wrap items-end gap-10" data-testid="detail-stats">
            <Stat value={Math.round(wf.height_m)} unit="m" label="Waterfall height" />
          </section>
        )}

        {/* Body grid */}
        <section className="mt-10 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-10">
            {tab === "overview" && (
              <>
                <article className="prose prose-stone max-w-none">
                  <p className="whitespace-pre-line text-stone-800 leading-[1.7] text-[16px]" data-testid="waterfall-description">
                    {wf.description}
                  </p>
                </article>
                {/* AI-enriched practical info: swimming, dogs, official site, best time, nearby */}
                <VisitInfo wf={wf} />
                {embed && (
                  <div>
                    <h2 className="text-xl font-extrabold text-stone-900 mb-3">Watch the falls</h2>
                    <div className="aspect-video rounded-2xl overflow-hidden">
                      <iframe src={embed} title={`${wf.name} video`} allow="encrypted-media" allowFullScreen className="w-full h-full" data-testid="youtube-embed" />
                    </div>
                  </div>
                )}
              </>
            )}
            {tab === "reviews" && (
              <ReviewsSection slug={wf.slug} waterfallName={wf.name} embedded />
            )}
            {tab === "nearby" && data.related?.length > 0 && (
              <div>
                <h2 className="text-xl font-extrabold mb-5">Nearby waterfalls</h2>
                <div className="grid sm:grid-cols-2 gap-6">
                  {data.related.map((w) => <WaterfallCard key={w.id} wf={w} portrait={false} />)}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar — Top sights */}
          <aside className="space-y-5">
            <div className="rounded-2xl border border-stone-200 p-5">
              <h3 className="text-lg font-extrabold mb-3">Nearby</h3>
              <ul className="divide-y divide-stone-100">
                {(data.related || []).slice(0, 4).map((w) => (
                  <li key={w.id}>
                    <Link to={`/waterfalls/${w.slug}`} className="flex items-center justify-between py-3 hover:underline" data-testid={`top-sight-${w.slug}`}>
                      <div>
                        <p className="font-semibold text-stone-900">{w.name}</p>
                        <p className="text-xs text-stone-500">{w.city || w.state}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-stone-400" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-2xl border border-stone-200 p-5">
              <h3 className="text-lg font-extrabold mb-3">Address</h3>
              <p className="text-sm text-stone-700 mb-3 flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" /><span>{wf.address}</span></p>
              <p className="text-xs text-stone-500">{wf.lat?.toFixed(4)}, {wf.lng?.toFixed(4)}</p>
            </div>
          </aside>
        </section>

        {/* Site-specific safety — FAQ accordion style */}
        <div className="mt-4"><SafetyAccordion siteSpecific={wf.safety_notes} /></div>

        {/* Questions & Answers */}
        <QuestionsSection slug={wf.slug} />

        {/* Related grid (always at bottom) */}
        {data.related?.length > 0 && tab !== "nearby" && (
          <section className="mt-12" data-testid="related-section">
            <h2 className="text-2xl md:text-3xl font-extrabold text-stone-900 mb-6">More to explore nearby</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-x-5 gap-y-10">
              {data.related.map((w) => <WaterfallCard key={w.id} wf={w} />)}
            </div>
          </section>
        )}
      </div>

      {/* Floating action pill */}
      <div className="floating-actions" data-testid="floating-actions">
        <button
          onClick={toggleSave}
          disabled={savedBusy}
          className={`btn-at !px-5 disabled:opacity-60 ${saved ? "bg-emerald-50 text-emerald-700 border border-emerald-200" : "btn-at-green"}`}
          data-testid="action-save"
        >
          <Heart className={`w-4 h-4 mr-1 ${saved ? "fill-emerald-700" : ""}`} /> {saved ? "Saved" : "Save"}
        </button>
        <a
          href={`https://www.google.com/maps/dir/?api=1&destination=${wf.lat},${wf.lng}`}
          target="_blank" rel="noreferrer"
          data-testid="action-directions"
          className="btn-at text-white"
          style={{ background: "hsl(150 14% 18%)" }}
        >
          <Navigation className="w-4 h-4 mr-1" /> Get directions
        </a>
        <button
          onClick={handleShare}
          className="btn-at text-white hidden md:inline-flex"
          style={{ background: "hsl(150 14% 18%)" }}
          data-testid="action-share-mobile"
        >
          <Share2 className="w-4 h-4 mr-1" /> Share
        </button>
      </div>
    </main>
  );
}

function Stat({ value, unit, label }) {
  return (
    <div>
      <div className="flex items-baseline gap-1">
        <span className="text-4xl font-extrabold tracking-tight text-stone-900">{value}</span>
        <span className="text-base text-stone-600 font-semibold">{unit}</span>
      </div>
      <p className="text-xs text-stone-500 mt-1">{label}</p>
    </div>
  );
}
